import { LibrarySymbolInfo, Timezone, VisiblePlotsSet } from '@frontends/feature-charting-library/datafeed-api';
import { resolutionsMapper } from '@frontends/feature-tv-parts';

export interface RequestParams {
  [paramName: string]: string | string[] | number;
}

export interface UdfResponse {
  s: string;
}

export interface UdfOkResponse extends UdfResponse {
  s: 'ok';
}

export interface UdfErrorResponse {
  errmsg: string;
  s: 'error';
}

/**
 * If you want to enable logs from datafeed set it to `true`
 */
const isLoggingEnabled = false;
export function logMessage(message: string): void {
  if (isLoggingEnabled) {
    const now = new Date();
    console.log(`${now.toLocaleTimeString()}.${now.getMilliseconds()}> ${message}`);
  }
}

export function getErrorMessage(error: string | Error | undefined): string {
  if (error === undefined) {
    return '';
  } else if (typeof error === 'string') {
    return error;
  }

  return error.message;
}

export type StoredBar = { time: number; open: number; high: number; low: number; close: number };
export type StoredLastBars = { latest: StoredBar; prev: StoredBar | null };

export const LAST_BARS_KEY = 'lastBars';

export function loadLastBars(): StoredLastBars | null {
  const raw = localStorage.getItem(LAST_BARS_KEY);

  return raw ? (JSON.parse(raw) as StoredLastBars) : null;
}

export function saveLastBarsIfNewer(candidate: StoredBar): boolean {
  const saved = loadLastBars();

  if (!saved) {
    localStorage.setItem(LAST_BARS_KEY, JSON.stringify({ latest: candidate, prev: null } satisfies StoredLastBars));

    return true;
  }

  if (candidate.time > saved.latest.time) {
    localStorage.setItem(
      LAST_BARS_KEY,
      JSON.stringify({ latest: candidate, prev: saved.latest } satisfies StoredLastBars),
    );

    return true;
  }

  if (candidate.time === saved.latest.time) {
    localStorage.setItem(
      LAST_BARS_KEY,
      JSON.stringify({ latest: candidate, prev: saved.prev } satisfies StoredLastBars),
    );

    return true;
  }

  return false;
}
export function clearLastBars(): void {
  localStorage.removeItem(LAST_BARS_KEY);
}

export const resolutionToSeconds: Record<string, number> = {
  ...Object.fromEntries(Object.entries(resolutionsMapper).map(([resolution, minutes]) => [resolution, minutes * 60])),
  D: 86400,
  '2D': 172800,
  '3D': 259200,
  W: 604800,
  '3W': 1814400,
  M: 2592000,
  '6M': 15552000,
};

export const symbolConf = {
  AAPL: {
    name: 'AAPL',
    'exchange-traded': 'NasdaqNM',
    'exchange-listed': 'NasdaqNM',
    timezone: 'America/New_York' as Timezone,
    minmov: 1,
    minmov2: 0,
    pointvalue: 1,
    session: '0930-1630',
    visible_plots_set: 'ohlcv' as VisiblePlotsSet,
    description: 'Apple Inc.',
    type: 'stock',
    //"supported_resolutions": ["D", "2D", "3D", "W", "3W", "M", "6M"] as ResolutionString[],
    pricescale: 100,
    ticker: 'AAPL',
    logo_urls: ['https://s3-symbol-logo.tradingview.com/apple.svg'] as [string],
    exchange_logo: 'https://s3-symbol-logo.tradingview.com/country/US.svg',
    // "has_intraday": true,
    // "has_weekly_and_monthly": true,
    // "has_daily":true,
    // "daily_multipliers": ["1", "2", "3"],
    // "monthly_multipliers": ["1", "2", "3", "6"],
    // "weekly_multipliers": ["1", "2", "3", "6"]
  },
  BTCUSDT: {
    name: 'BTCUSDT',
    'exchange-traded': 'NasdaqNM',
    'exchange-listed': 'NasdaqNM',
    timezone: 'America/New_York' as Timezone,
    minmov: 1,
    minmov2: 0,
    pointvalue: 1,
    session: '24x7',
    visible_plots_set: 'ohlcv' as VisiblePlotsSet,
    description: 'BTCUSDT',
    type: 'crypto',
    //"supported_resolutions": ["D", "2D", "3D", "W", "3W", "M", "6M"] as ResolutionString[],
    pricescale: 100,
    ticker: 'BTCUSDT',
    logo_urls: ['https://s3-symbol-logo.tradingview.com/apple.svg'] as [string],
    exchange_logo: 'https://s3-symbol-logo.tradingview.com/country/US.svg',
    // "has_intraday": true,
    // "has_weekly_and_monthly": true,
    // "has_daily":true,
    // "daily_multipliers": ["1", "2", "3"],
    // "monthly_multipliers": ["1", "2", "3", "6"],
    // "weekly_multipliers": ["1", "2", "3", "6"]
  },
};

const MINUTES_IN_DAY = 1440;
const MINUTES_IN_WEEK = 7 * MINUTES_IN_DAY;

interface SessionWindow {
  close: number;
  open: number;
}

function getTimeInTimezone(
  timestampSec: number,
  timezone?: string,
): { dayOfWeek: number; hours: number; minutes: number } {
  const date = new Date(timestampSec * 1000);

  if (!timezone) {
    return {
      dayOfWeek: date.getUTCDay(),
      hours: date.getUTCHours(),
      minutes: date.getUTCMinutes(),
    };
  }

  const localized = new Date(date.toLocaleString('en-US', { timeZone: timezone }));

  return {
    dayOfWeek: localized.getDay(),
    hours: localized.getHours(),
    minutes: localized.getMinutes(),
  };
}

function parseSessionWindows(session: string): SessionWindow[] {
  const windows: SessionWindow[] = [];
  const segments = session.split('|');

  for (const seg of segments) {
    const colonIdx = seg.lastIndexOf(':');
    let timePart: string;
    let dayChars: string;

    if (colonIdx !== -1) {
      timePart = seg.substring(0, colonIdx);
      dayChars = seg.substring(colonIdx + 1);
    } else {
      timePart = seg;
      dayChars = '23456';
    }

    const [openStr, closeStr] = timePart.split('-');
    const hasFFlag = openStr.endsWith('F');
    const openClean = openStr.replace('F', '');
    const openMin = parseInt(openClean.substring(0, 2)) * 60 + parseInt(openClean.substring(2, 4));
    const closeMin = parseInt(closeStr.substring(0, 2)) * 60 + parseInt(closeStr.substring(2, 4));

    for (const ch of dayChars) {
      const tvDay = parseInt(ch); // TradingView: 1=Sun..7=Sat
      const dayOff = tvDay - 1; // JS-style: 0=Sun..6=Sat

      let openMow: number;
      let closeMow: number;

      if (hasFFlag) {
        // F flag: session opens on the previous calendar day
        const openDayOff = dayOff === 0 ? 6 : dayOff - 1;
        openMow = openDayOff * MINUTES_IN_DAY + openMin;
        closeMow = dayOff * MINUTES_IN_DAY + closeMin;
      } else {
        openMow = dayOff * MINUTES_IN_DAY + openMin;
        closeMow = dayOff * MINUTES_IN_DAY + closeMin;

        if (openMin >= closeMin) {
          closeMow += MINUTES_IN_DAY;
        }
      }

      windows.push({ open: openMow, close: closeMow });
    }
  }

  return windows.sort((a, b) => a.open - b.open);
}

/**
 * Checks whether `date` (unix timestamp in seconds) falls within the symbol's
 * trading session. If it does, returns `date` unchanged. If it doesn't, returns
 * the timestamp of the most recent session close.
 */
export const floorTo = (value: number, step: number, offset = 0): number =>
  Math.floor((value + offset) / step) * step - offset;

export function adjustBarTime(value: number, stepSec: number, chartSec: number): number {
  if (stepSec < 86400) {
    return floorTo(value, chartSec * 1000, 10800000);
  }

  if (stepSec >= 86400 && chartSec === 604800) {
    return floorTo(value, chartSec * 1000, 345600000); // Floor to sunday
  }

  return floorTo(value, chartSec * 1000);
}

export function getDateInSession(symbolInfo: LibrarySymbolInfo, date: number): number {
  const session = symbolInfo.session;

  if (!session || session === '24x7') return date;

  const windows = parseSessionWindows(session);

  if (windows.length === 0) return date;

  const { dayOfWeek, hours, minutes } = getTimeInTimezone(date, symbolInfo.timezone);
  const mow = dayOfWeek * MINUTES_IN_DAY + hours * 60 + minutes;

  for (const sessionWindow of windows) {
    if (mow >= sessionWindow.open && mow < sessionWindow.close) {
      return date;
    }
  }

  let bestClose = -1;

  for (const sessionWindow of windows) {
    if (sessionWindow.close <= mow && sessionWindow.close > bestClose) {
      bestClose = sessionWindow.close;
    }
  }

  if (bestClose === -1) {
    // Before all sessions this week — wrap to the last close of the previous week
    const lastWindow = windows[windows.length - 1];
    const diff = MINUTES_IN_WEEK - lastWindow.close + mow;

    return date - diff * 60;
  }

  return date - (mow - bestClose) * 60;
}
